import { KNOCKOUT_FIXTURE_SLOTS, KNOCKOUT_MATCHES_PER_DAY, BRACKET_MATCH_ORDER } from '../data/constants.js';
import { findTeamName } from './helpers.js';
import { gt } from '../i18n/index.js';

export function fixtureDayKey(fixture) {
  if (!fixture) return '';
  return fixtureDateKey(fixture.date);
}

export function fixtureDateKey(date) {
  return date ? String(date).slice(0, 10) : '';
}

export function formatFixtureDateLabel(value) {
  if (!value) return gt('fixture.noDate');
  const [year, month, day] = String(value).split('-');
  return year && month && day ? `${year}-${month}-${day}` : value;
}

export function fixtureSortValue(fixture) {
  return fixture.date || `z-${stageOrder(fixture.stage)}-${String(knockoutMatchdayNumber(fixture)).padStart(2, '0')}-${fixture.id}`;
}

export function stageOrder(stage) {
  return {
    'Round of 32': 1,
    'Round of 16': 2,
    'Quarter-final': 3,
    'Semi-final': 4,
    'Third Place': 5,
    Final: 6,
  }[stage] || 9;
}

export function knockoutMatchdayNumber(fixture) {
  const perDay = KNOCKOUT_MATCHES_PER_DAY[fixture?.stage] || 1;
  const matchNumber = Number(String(fixture?.id || '').match(/(\d+)$/)?.[1] || 1);
  return Math.max(1, Math.ceil(matchNumber / perDay));
}

export function adjacentFixtureDate(dates, activeDate, direction) {
  const index = dates.indexOf(activeDate);
  if (index < 0) return '';
  return dates[index + direction] || '';
}

export function tournamentFixtureDates(nextRun, manifestFixtures) {
  return [...new Set(allKnownFixtures(nextRun, manifestFixtures).map((fixture) => fixtureDateKey(fixture.date)).filter(Boolean))];
}

export function fixtureDayItems(nextRun, day, manifestFixtures) {
  const matchesById = new Map(nextRun.matches.map((match) => [match.id, match]));
  const fixtures = allKnownFixtures(nextRun, manifestFixtures);
  return fixtures
    .filter((fixture) => fixtureDayKey(fixture) === day)
    .map((fixture) => ({ fixture, match: matchesById.get(fixture.id) || null }));
}

export function allKnownFixtures(nextRun, manifestFixtures) {
  const list = [
    ...nextRun.knockoutRounds.flatMap((round) => round.matches),
    ...(nextRun.nextMatch ? [nextRun.nextMatch] : []),
    ...projectedKnockoutFixtures(nextRun, manifestFixtures),
    ...(manifestFixtures || []),
  ];
  return list
    .filter((fixture, index) => list.findIndex((item) => item.id === fixture.id) === index)
    .sort((a, b) => fixtureSortValue(a).localeCompare(fixtureSortValue(b)));
}

export function projectedKnockoutFixtures(nextRun, manifestTeams) {
  if (!nextRun?.groups?.some((group) => group.qualifiers?.length)) return [];
  const matchesById = knownKnockoutFixturesById(nextRun);
  const projectedByMatchNo = new Map();
  const usedThirds = new Set();
  const teamNameFn = (id) => findTeamName(manifestTeams, id);
  return KNOCKOUT_FIXTURE_SLOTS.map((slot) => {
    const existing = matchesById.get(`match-${slot.id}`);
    if (existing) {
      projectedByMatchNo.set(slot.id, existing);
      return existing;
    }
    const homeId = resolveKnockoutSlotSource(slot.home, nextRun, projectedByMatchNo, usedThirds, teamNameFn);
    if (slot.home[0] === 'third' && homeId !== 'tbd') usedThirds.add(homeId);
    const awayId = resolveKnockoutSlotSource(slot.away, nextRun, projectedByMatchNo, usedThirds, teamNameFn);
    if (slot.away[0] === 'third' && awayId !== 'tbd') usedThirds.add(awayId);
    const fixture = {
      id: `match-${slot.id}`,
      stage: slot.stage,
      group: null,
      date: slot.date,
      venue: slot.venue,
      homeTeamId: homeId,
      awayTeamId: awayId,
      homeTeamName: teamNameFn(homeId) || 'TBD',
      awayTeamName: teamNameFn(awayId) || 'TBD',
      pending: true,
    };
    projectedByMatchNo.set(slot.id, fixture);
    return fixture;
  });
}

export function knownKnockoutFixturesById(nextRun) {
  const entries = [
    ...nextRun.matches.filter((match) => match.stage !== 'Group Stage'),
    ...nextRun.knockoutRounds.flatMap((round) => round.matches),
    ...(nextRun.nextMatch && nextRun.nextMatch.stage !== 'Group Stage' ? [nextRun.nextMatch] : []),
  ];
  return new Map(entries.map((match) => [
    match.id,
    {
      ...match,
      pending: !match.score,
    },
  ]));
}

export function resolveKnockoutSlotSource(source, nextRun, projectedByMatchNo, usedThirds, teamNameFn) {
  const [kind, first, second] = source;
  if (kind === 'rank') {
    return nextRun.groups.find((group) => group.id === first)?.standings?.[Number(second) - 1]?.teamId || 'tbd';
  }
  if (kind === 'third') {
    const candidate = first
      .map((groupId) => nextRun.groups.find((group) => group.id === groupId)?.standings?.[2])
      .filter((standing) => standing && nextRun.groups.some((group) => group.qualifiers?.includes(standing.teamId)) && !usedThirds.has(standing.teamId))
      .sort(bestThirdStandingSort)[0];
    const fallback = nextRun.groups
      .map((group) => group.standings?.[2])
      .filter((standing) => standing && nextRun.groups.some((group) => group.qualifiers?.includes(standing.teamId)) && !usedThirds.has(standing.teamId))
      .sort(bestThirdStandingSort)[0];
    const usedCandidate = first
      .map((groupId) => nextRun.groups.find((group) => group.id === groupId)?.standings?.[2])
      .filter(Boolean)
      .sort(bestThirdStandingSort)[0];
    const usedFallback = nextRun.groups.map((group) => group.standings?.[2]).filter(Boolean).sort(bestThirdStandingSort)[0];
    return (candidate || fallback || usedCandidate || usedFallback)?.teamId || 'tbd';
  }
  const prior = projectedByMatchNo.get(Number(first)) || nextRun.matches.find((match) => match.id === `match-${first}`);
  if (!prior?.score) return 'tbd';
  const winnerId = prior.winnerTeamId || prior.penaltyWinnerTeamId;
  if (kind === 'winner') return winnerId || 'tbd';
  if (!winnerId) return 'tbd';
  return winnerId === prior.homeTeamId ? prior.awayTeamId : prior.homeTeamId;
}

export function bestThirdStandingSort(a, b) {
  return Number(b.points || 0) - Number(a.points || 0)
    || Number(b.goalDifference || 0) - Number(a.goalDifference || 0)
    || Number(b.goalsFor || 0) - Number(a.goalsFor || 0)
    || String(a.teamName || '').localeCompare(String(b.teamName || ''));
}

export function selectedFixtures(selected, nextRun, manifestFixtures) {
  const groupFixtures = manifestFixtures
    .filter((fixture) => fixture.homeTeamId === selected || fixture.awayTeamId === selected)
    .map((fixture) => ({ fixture, match: nextRun.matches.find((match) => match.id === fixture.id) }));
  const knockout = nextRun.selectedPath
    .filter((match) => match.stage !== 'Group Stage')
    .map((match) => ({ fixture: match, match }));
  return [...groupFixtures, ...knockout];
}

export function nextOwnMatch(nextRun, manifestFixtures) {
  const selected = nextRun.selectedTeamId;
  const fixtures = selectedFixtures(selected, nextRun, manifestFixtures);
  return fixtures.find((item) => !item.match)?.fixture || (nextRun.nextMatch &&
    (nextRun.nextMatch.homeTeamId === selected || nextRun.nextMatch.awayTeamId === selected)
    ? nextRun.nextMatch
    : null);
}

export function fixtureDayHasOwnPendingMatch(nextRun, day, manifestFixtures) {
  if (!nextRun || !day) return false;
  const selected = nextRun.selectedTeamId;
  const matchesById = new Map(nextRun.matches.map((match) => [match.id, match]));
  return allKnownFixtures(nextRun, manifestFixtures).some((fixture) =>
    fixtureDayKey(fixture) === day &&
    !matchesById.has(fixture.id) &&
    (fixture.homeTeamId === selected || fixture.awayTeamId === selected),
  );
}

export function bracketRounds(nextRun, manifestTeams) {
  const sizes = { 'Round of 32': 16, 'Round of 16': 8, 'Quarter-final': 4, 'Semi-final': 2, 'Third Place': 1, Final: 1 };
  const projected = projectedKnockoutFixtures(nextRun, manifestTeams);
  return ['Round of 32', 'Round of 16', 'Quarter-final', 'Semi-final', 'Third Place', 'Final'].map((name) => {
    const order = BRACKET_MATCH_ORDER[name] || [];
    const matches = order.map((matchNo) => {
      const fixture = projected.find((item) => item.id === `match-${matchNo}`);
      const existing = nextRun.matches.find((item) => item.id === `match-${matchNo}`);
      return existing || (fixture ? fixtureToPendingMatch(fixture) : null);
    }).filter((match) => match !== undefined);
    while (matches.length < sizes[name]) matches.push(null);
    return { name, matches };
  });
}

export function fixtureToPendingMatch(fixture) {
  return {
    ...fixture,
    score: null,
    pending: true,
    winnerTeamId: null,
    penaltyWinnerTeamId: null,
  };
}

export function tournamentFixtures(nextRun, manifestFixtures) {
  const playedIds = new Set(nextRun.matches.map((match) => match.id));
  const groupFixtures = manifestFixtures.map((fixture) => ({
    fixture,
    match: nextRun.matches.find((match) => match.id === fixture.id),
  }));
  const knockout = nextRun.knockoutRounds.flatMap((round) => round.matches).map((match) => ({ fixture: match, match }));
  const upcoming = nextRun.nextMatch && !playedIds.has(nextRun.nextMatch.id) ? [{ fixture: nextRun.nextMatch, match: null }] : [];
  return [...groupFixtures, ...knockout, ...upcoming]
    .filter((item, index, list) => list.findIndex((other) => other.fixture.id === item.fixture.id) === index)
    .filter((item) => item.match || item.fixture.id === nextRun.nextMatch?.id)
    .slice(-18);
}
